import { useEffect, useRef, useState } from "react";
import { Sparkles, Check, Crown, ShieldCheck, Coins, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import api, { formatApiErrorDetail } from "../api";

const TIERS = [
  {
    id: "personal",
    name: "Personal Plus",
    arabicSubtitle: "البلس الشخصي",
    price: "10.99",
    duration: "شهرياً",
    currency: "ر.س",
    cta: "اشترك الآن",
    color: "border-gold-500/40 bg-gradient-to-b from-gold-500/5 via-transparent to-transparent",
    accent: "#FFCC00",
    badge: "للاعب الفردي والمحترف",
    icon: <Sparkles size={22} />,
    perks: [
      "صورة شخصية مخصصة (≤2MB)",
      "بانر خلفية كامل للبروفايل (≤3MB)",
      "اختيار لون مميز يظهر في ملفك والشات",
      "شارة Plus حصرية بجانب اسمك",
      "تجربة مجانية لمدة 3 أيام عند التسجيل",
    ],
  },
  {
    id: "clan",
    name: "Clan Plus",
    arabicSubtitle: "بلس الكلانات الرسمية",
    price: "26.99",
    duration: "شهرياً",
    currency: "ر.س",
    cta: "اشترك للكلان",
    color: "border-emerald-500/40 bg-gradient-to-b from-emerald-500/5 via-transparent to-transparent",
    accent: "#22D3A4",
    badge: "للقادة وأبطال الكلانات",
    icon: <Crown size={22} />,
    perks: [
      "زيادة سعة أعضاء الكلان من 6 إلى 9 لاعب",
      "تعيين نائبَين للقائد",
      "أولوية الوصول والتسجيل للبطولات والحصرية",
      "هالة ذهبية متحركة على اسم الكلان في الترتيب",
      "إعلان مميز وتلقائي عند بدء مباراة كلانك",
    ],
  },
];

export default function PersonalPlusPage() {
  const { user, refresh } = useAuth();

  const [busyRivTier, setBusyRivTier] = useState(null);
  const checkoutOperation = useRef(null);

  const mockCheckout = () => {
    toast.info("بوابة الدفع غير مفعلة حالياً. يمكنك تفعيل الاشتراك بنقاط RIV.");
  };

  const checkoutWithRiv = async (tierId) => {
    if (busyRivTier) return;
    if (checkoutOperation.current?.tier !== tierId) checkoutOperation.current = { tier: tierId, id: crypto.randomUUID() };
    setBusyRivTier(tierId);
    try {
      const plan = tierId === "personal" ? "person_plus" : "clan_plus";
      const { data } = await api.post("/billing/checkout", {
        plan,
        provider: "riv_points",
        pay_with_riv_points: true,
        operation_id: checkoutOperation.current.id,
      });
      checkoutOperation.current = null;
      toast.success(`تمت عملية الشراء بنجاح عبر نقاط RIV! • الرصيد المتبقي: ${data?.riv_points ?? "--"} RIV`);
      await refresh().catch(() => toast.info("تم الشراء؛ حدّث الصفحة لعرض رصيدك الجديد."));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyRivTier(null);
    }
  };

  const trialActive = !!user?.is_personal_plus;

  return (
    <div className="space-y-12 pb-24 max-w-5xl mx-auto px-4" dir="rtl">
      
      {/* ── الهيدر الرئيسي المطور للمتجر ── */}
      <header className="text-center max-w-3xl mx-auto space-y-3 pt-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400 text-xs font-bold tracking-widest uppercase shadow-sm">
          <Sparkles size={14} className="animate-pulse" /> اشتراكات RIVALS
        </div>
        <h1 className="font-display font-black text-3xl md:text-5xl text-white tracking-tight">
          اشتراكات الحساب والكلان
        </h1>
        <p className="text-white/60 text-xs md:text-sm leading-relaxed max-w-xl mx-auto">
          اختر باقة البلس المناسبة لحسابك أو كلانك واطّلع على الميزانية الشهرية المعلنة.
        </p>

        {trialActive && user?.personal_plus_until && (
          <div data-testid="plus-trial-banner" className="inline-flex items-center gap-2 bg-gold-500/15 border border-gold-500/30 rounded-2xl px-4 py-2 mt-1 text-xs font-bold text-gold-400 shadow-sm">
            <Crown size={15} /> باقة Personal Plus نشطة حتى {new Date(user.personal_plus_until).toLocaleDateString("ar")}
          </div>
        )}

        {/* أزرار الأرصدة الحالية */}
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <div className="px-4 py-2 rounded-2xl bg-[#141417] border border-gold-500/30 text-gold-400 text-xs font-bold flex items-center gap-2 shadow-inner">
            <Coins size={15} /> نقاط RIV: <span className="font-mono text-sm">{Number(user?.riv_points || 0)} RIV</span>
          </div>
          <div className="px-4 py-2 rounded-2xl bg-[#141417] border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center gap-2 shadow-inner">
            <Gamepad2 size={15} /> رصيد الجيمز: <span className="font-mono text-sm">{Number(user?.game_coins || 0)} Coin</span>
          </div>
        </div>
      </header>

      {/* ── عرض قسم باقات بلس ── */}
      {(
        <div className="space-y-10 animate-fadeIn">
          <section className="space-y-6">
            <div className="text-center space-y-1">
              <h2 className="font-display font-black text-2xl md:text-3xl text-white">باقات RIVALS PLUS الرسمية</h2>
              <p className="text-xs text-white/50">اختر الباقة المناسبة لتخصيص ملفك الشخصي أو قيادة كلانك باحترافية</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {TIERS.map((t) => (
                <article
                  key={t.id}
                  data-testid={`plus-tier-${t.id}`}
                  className={`relative bg-[#111113]/90 border ${t.color} rounded-3xl p-6 md:p-7 flex flex-col justify-between overflow-hidden shadow-xl transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="h-11 w-11 rounded-2xl grid place-items-center shadow-md shrink-0 border border-white/10" style={{ background: `${t.accent}1a`, color: t.accent }}>
                        {t.icon}
                      </div>
                      <span className="text-[10px] uppercase font-bold tracking-widest px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-white/60">
                        {t.badge}
                      </span>
                    </div>

                    <div>
                      <h3 className="font-display font-black text-xl text-white">{t.name}</h3>
                      <div className="text-white/50 text-xs font-bold mt-0.5">{t.arabicSubtitle}</div>
                    </div>

                    <div className="flex items-baseline gap-1 pt-1">
                      <span className="font-display font-black text-3xl text-white" style={{ color: t.accent }}>{t.price}</span>
                      <span className="text-white/50 text-xs font-bold">{t.currency} / {t.duration}</span>
                    </div>

                    <ul className="space-y-2.5 pt-3 border-t border-white/10 text-xs">
                      {t.perks.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-white/80 leading-relaxed">
                          <Check size={14} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-2.5 pt-6">
                    <button
                      data-testid={`cta-${t.id}`}
                      disabled={true}
                      onClick={() => mockCheckout(t.id)}
                      className="w-full py-2.5 rounded-xl font-black text-black text-xs disabled:opacity-60 hover:opacity-90 transition shadow-lg cursor-pointer"
                      style={{ background: t.accent }}
                    >
                      الدفع النقدي غير متاح حالياً
                    </button>
                    
                    <button
                      data-testid={`cta-riv-${t.id}`}
                      disabled={!!busyRivTier}
                      onClick={() => checkoutWithRiv(t.id)}
                      className="w-full py-2.5 rounded-xl font-bold text-xs border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/15 disabled:opacity-60 transition flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Coins size={14} />
                      {busyRivTier === t.id ? "جاري الخصم..." : `الدفع بنقاط RIV (${t.id === "personal" ? 11 : 27} RIV)`}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 md:p-7 max-w-3xl mx-auto shadow-xl">
            <h2 className="font-display font-black text-lg text-white mb-4 flex items-center gap-2">
              <ShieldCheck size={18} className="text-gold-500" /> مقارنة سريعة بين مزايا بلس
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-white/40 uppercase tracking-widest border-b border-white/10 pb-2">
                    <th className="text-right pb-2">الميزة الرئيسية</th>
                    <th className="text-center pb-2 text-emerald-400">Clan Plus</th>
                    <th className="text-center pb-2 text-gold-400">Personal Plus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <ComparisonRow label="تخصيص الأفاتار والبانر واللون الشخصي" left={false} right />
                  <ComparisonRow label="شارة Plus مميزة في الشات والبروفايل" left={false} right />
                  <ComparisonRow label="زيادة سعة أعضاء الكلان إلى 12 لاعب" left right={false} />
                  <ComparisonRow label="تعيين نائبَين للقائد بدل نائب واحد" left right={false} />
                  <ComparisonRow label="هالة ذهبية متحركة وإعلانات المباريات" left right={false} />
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      <PublicBudget user={user} />
    </div>
  );
}

function ComparisonRow({ label, left, right }) {
  return (
    <tr className="hover:bg-white/[0.02] transition">
      <td className="py-3 text-white/80 font-medium">{label}</td>
      <td className="text-center py-3">{left ? <Check size={15} className="text-gold-500 inline" /> : <span className="text-white/20">—</span>}</td>
      <td className="text-center py-3">{right ? <Check size={15} className="text-emerald-400 inline" /> : <span className="text-white/20">—</span>}</td>
    </tr>
  );
}

function PublicBudget({ user }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [retry, setRetry] = useState(0);
  const staff = ["owner", "admin"].includes(user?.role);
  const fields = { server: "استضافة السيرفر", domain: "النطاق", bot: "خدمات البوت", tournaments: "تنظيم البطولات", prizes: "الجوائز" };
  const money = n => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(n || 0));
  const inputClass = "w-full bg-black/30 border border-white/15 rounded-xl px-4 py-3 text-white focus:border-gold-500 outline-none disabled:opacity-50";
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    api.get("/billing/outflow", { signal: controller.signal, timeout: 10000 }).then(({data}) => {
      if (!controller.signal.aborted) setData(data);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [retry]);
  const save = async e => {
    e.preventDefault();
    if (busy || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const {data: fresh} = await api.put("/billing/outflow", { ...draft, reason });
      setData(fresh); setDraft(null); setReason(""); toast.success("تم نشر الميزانية");
    } catch(e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "تعذر حفظ الميزانية"); }
    finally { setBusy(false); }
  };
  const total = data ? Object.keys(fields).reduce((sum,k) => sum + Number(data[k] || 0), 0) : 0;
  const net = Number(data?.income_sar || 0) - total;
  return <section id="public-budget" data-testid="public-budget-panel" className="overflow-hidden rounded-3xl border border-white/10 bg-[#111113] shadow-xl" dir="rtl">
    <div className="p-6 md:p-8 border-b border-white/10 flex flex-wrap justify-between items-center gap-4">
      <div><div className="text-[11px] tracking-widest text-gold-400 font-bold mb-2">RIVALS • الشفافية المالية</div><h2 className="text-2xl font-black text-white">الميزانية العلنية</h2><p className="text-sm text-white/45 mt-2">الإيرادات والمصروفات الشهرية المعلنة من الإدارة.</p></div>
      <div className="flex items-center gap-3"><span className="rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs text-white/60">{data?.period || "الميزانية الشهرية"}</span>{staff && data && !draft && <button type="button" onClick={() => setDraft({...data})} className="rounded-xl bg-gold-500 text-black font-bold px-5 py-3 text-sm hover:bg-gold-400">{data.configured ? "تعديل الميزانية" : "إضافة ميزانية الشهر"}</button>}</div>
    </div>
    {error ? <div className="p-8 text-center text-white/60">تعذر تحميل الميزانية. <button onClick={() => setRetry(n => n + 1)} className="text-gold-400 underline px-2">إعادة المحاولة</button></div> : !data ? <div className="p-8 grid sm:grid-cols-3 gap-4 animate-pulse">{[1,2,3].map(i => <div key={i} className="h-28 rounded-2xl bg-white/5" />)}</div> : draft ? <form onSubmit={save} className="p-6 md:p-8 space-y-6">
      <div><h3 className="text-lg font-bold">إعداد ميزانية {data.period}</h3><p className="text-xs text-white/50 mt-1">تظهر المبالغ للزوار بعد الحفظ. جميع القيم بالريال السعودي.</p></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[["income_sar","الإيرادات"], ...Object.entries(fields)].map(([key,label]) => <label key={key} className="space-y-2 block"><span className="text-sm text-white/70">{label}</span><div className="relative"><input type="number" min="0" max="1000000" step="0.01" required disabled={busy} value={draft[key]} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} className={inputClass} /></div></label>)}</div>
      <label className="block space-y-2"><span className="text-sm text-white/70">سبب التحديث</span><input required minLength={3} maxLength={300} disabled={busy} value={reason} onChange={e => setReason(e.target.value)} placeholder="مثال: تسجيل إيرادات ومصاريف الشهر" className={inputClass} /></label>
      <div className="flex flex-wrap gap-3"><button disabled={busy || reason.trim().length < 3} className="bg-gold-500 text-black rounded-xl px-6 py-3 font-bold disabled:opacity-40">{busy ? "جاري الحفظ..." : "حفظ ونشر الميزانية"}</button><button type="button" disabled={busy} onClick={() => setDraft(null)} className="border border-white/15 rounded-xl px-6 py-3 text-white/70">إلغاء</button></div>
    </form> : !data.configured ? <div className="px-6 py-14 text-center max-w-xl mx-auto"><div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gold-500/10 text-gold-400 flex items-center justify-center"><Coins size={28} /></div><h3 className="font-bold text-lg">ميزانية هذا الشهر قيد الإعداد</h3><p className="text-sm leading-7 text-white/50 mt-3">{staff ? "ابدأ بإضافة إيرادات الشهر ومصروفاته من الزر أعلاه. بعد الحفظ ستظهر هنا لوحة الميزانية للجميع." : "ستظهر تفاصيل الإيرادات والمصروفات هنا بعد نشرها من الإدارة."}</p></div> : <div className="p-6 md:p-8 space-y-8">
      <div className="grid sm:grid-cols-3 gap-4">{[["الإيرادات",data.income_sar,"text-emerald-300","bg-emerald-500/5 border-emerald-500/20"],["المصروفات",total,"text-rose-300","bg-rose-500/5 border-rose-500/20"],["صافي الشهر",net,net < 0 ? "text-rose-300" : "text-gold-400","bg-gold-500/5 border-gold-500/20"]].map(([label,value,color,bg]) => <div key={label} className={`p-5 rounded-2xl border ${bg}`}><p className="text-xs text-white/60 mb-4">{label}</p><p className={`text-2xl md:text-3xl font-black tabular-nums ${color}`}>{money(value)} <span className="text-xs font-normal text-white/40">ر.س</span></p></div>)}</div>
      <div><h3 className="font-bold mb-5">توزيع المصروفات</h3><div className="space-y-5">{Object.entries(fields).map(([key,label]) => <div key={key}><div className="flex justify-between text-sm mb-2"><span className="text-white/65">{label}</span><span className="font-bold tabular-nums">{money(data[key])} <span className="text-white/35 text-xs">ر.س</span></span></div><div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-gold-500/70" style={{width:`${total ? Number(data[key] || 0) / total * 100 : 0}%`}} /></div></div>)}</div></div>
      <p className="text-xs text-white/35 border-t border-white/10 pt-4">آخر تحديث: {data.updated_at ? new Date(data.updated_at).toLocaleString("ar-SA") : "—"}</p>
    </div>}
  </section>;
}
